import {
    AssistantsSignOff,
    OperationActionSignOff,
    SignOffDialogConfig,
} from '@data-terminal/shared-models/dialogs/sign-off-dialog';
import { Machine, SignOffMachine } from '@data-terminal/shared-models/machine';
import { TimeModeEntry } from '@data-terminal/shared-models/time-mode-entry.interface';
import { isCurrentUserAnOperator } from '@data-terminal/utils/functions';

export function shouldDisplayDialog(
    userId: string,
    userMachines: Machine[],
    unsubmittedTimeEntries: TimeModeEntry[]
): boolean {
    if (hasMoreThanOneMachine(userMachines)) {
        return true;
    }

    if (hasTeamMembers(userMachines[0])) {
        return isCurrentUserAnOperator(userMachines[0], userId);
    }

    if (hasUnsubmittedTimeEntries(unsubmittedTimeEntries)) {
        return true;
    }

    return isOperationActivityRunning(userMachines[0]);
}

export function hasMoreThanOneMachine(machines: Machine[]): boolean {
    return machines.length > 1;
}

export function hasTeamMembers(machine: Machine): boolean {
    return machine.signedOnUsers.length > 1;
}

export function isOperationActivityRunning(machine: Machine): boolean {
    return !!machine.runningOperation?.opName;
}

export function hasUnsubmittedTimeEntries(unsubmittedTimeEntries: TimeModeEntry[]): boolean {
    return !!unsubmittedTimeEntries.length;
}

export function hasUnsubmittedTimeEntriesForSignOffMachines(
    signOffMachines: SignOffMachine[],
    unsubmittedTimeEntries: TimeModeEntry[]
): boolean {
    const selectedDeviceIds = new Set(signOffMachines.map((signOffMachine) => signOffMachine.machineId));
    return unsubmittedTimeEntries.some((entry) => selectedDeviceIds.has(entry.deviceId));
}

export function shouldHandleOperationBecauseAssistantsWillBeLoggedOff(signOffMachine: SignOffMachine): boolean {
    return !signOffMachine.assistantsSignOff || signOffMachine.assistantsSignOff === AssistantsSignOff.LEAVE;
}

export function updatesOperation(currentMachine: SignOffMachine): SignOffMachine {
    return currentMachine.assistantsSignOff === AssistantsSignOff.STAY
        ? {
              ...currentMachine,
              operationAction: undefined,
              runningOperation: undefined,
              goodCount: undefined,
              wasteCount: undefined,
              comment: undefined,
          }
        : { ...currentMachine };
}

export function createInitialDialogProcessConfig(
    machines: Machine[],
    userId: string,
    unsubmittedTimeEntries: TimeModeEntry[],
    isEndShift = false
): SignOffDialogConfig {
    const hasTeam = hasAtLeastOneTeamInMachines(machines, userId);
    const hasOperationRunning = hasAtLeastOneOperationRunningInMachines(machines, userId);
    const multipleMachines = hasMoreThanOneMachine(machines);
    const hasUnsubmittedTimes = hasUnsubmittedTimeEntries(unsubmittedTimeEntries);
    return {
        workstations: isEndShift ? false : multipleMachines,
        employees: isEndShift ? false : hasTeam,
        operations: hasOperationRunning,
        timeTransfer: hasUnsubmittedTimes,
        summary: multipleMachines || hasOperationRunning || hasTeam || isEndShift || hasUnsubmittedTimes,
    };
}

function hasAtLeastOneTeamInMachines(machines: Machine[], userId: string): boolean {
    return machines.some((machine) => hasTeamMembers(machine) && isCurrentUserAnOperator(machine, userId));
}

function hasAtLeastOneOperationRunningInMachines(machines: Machine[], userId: string): boolean {
    return machines.some((machine) => isOperationActivityRunning(machine) && isCurrentUserAnOperator(machine, userId));
}

export function prepareUserMachinesToSignOff(machines: Machine[], userId: string): SignOffMachine[] {
    return machines.map((machine) => {
        if (isCurrentUserAnOperator(machine, userId)) {
            return createOperatorSignOffMachineObject(machine);
        } else {
            return createDefaultSignOffMachineObject(machine);
        }
    });
}

function createDefaultSignOffMachineObject(machine: Machine): SignOffMachine {
    return {
        machine: machine,
        machineId: machine.machineId,
        assistantsSignOff: undefined,
        runningOperation: undefined,
        wasteCount: undefined,
        goodCount: undefined,
        comment: undefined,
        newCaptain: undefined,
        newCaptainAsOperator: undefined,
        operationAction: undefined,
        isAssistant: true,
    };
}

function createOperatorSignOffMachineObject(machine: Machine): SignOffMachine {
    return {
        machine: machine,
        machineId: machine.machineId,
        assistantsSignOff: machine.signedOnUsers.length > 1 ? AssistantsSignOff.LEAVE : undefined,
        runningOperation: machine.runningOperation?.primaryKey ? machine.runningOperation?.primaryKey : undefined,
        operationAction: machine.runningOperation?.primaryKey ? OperationActionSignOff.PAUSE : undefined,
        wasteCount: undefined,
        goodCount: undefined,
        comment: undefined,
        newCaptain: undefined,
        newCaptainAsOperator: undefined,
        isAssistant: false,
    };
}

export function updateDialogProcessConfig(
    signOffMachine: SignOffMachine[],
    userId: string,
    assignedMachines: Machine[],
    unsubmittedTimeEntries: TimeModeEntry[] = []
): SignOffDialogConfig {
    const config: Omit<SignOffDialogConfig, 'summary'> = {
        workstations: hasMoreThanOneMachine(assignedMachines),
        employees: hasAtLeastOneTeamWithinSignOffMachinesList(signOffMachine, userId),
        operations: hasAtLeastOneOperationWithinSignOffMachinesList(signOffMachine, userId),
        timeTransfer: hasUnsubmittedTimeEntriesForSignOffMachines(signOffMachine, unsubmittedTimeEntries),
    };

    return {
        ...config,
        summary: Object.values(config).some(Boolean),
    };
}

function hasAtLeastOneTeamWithinSignOffMachinesList(signOffMachine: SignOffMachine[], userId: string): boolean {
    return signOffMachine.some((obj) => hasTeamMembers(obj.machine) && isCurrentUserAnOperator(obj.machine, userId));
}

function hasAtLeastOneOperationWithinSignOffMachinesList(signOffMachine: SignOffMachine[], userId: string): boolean {
    return signOffMachine.some(
        (obj) =>
            isOperationActivityRunning(obj.machine) &&
            shouldHandleOperationBecauseAssistantsWillBeLoggedOff(obj) &&
            isCurrentUserAnOperator(obj.machine, userId)
    );
}
